import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { PublishWhenControl, type PublishWhen } from '@/components/publishing/PublishWhenControl';
import {
  TikTokCreatorStatus, TikTokPostInfoFields,
} from '@/components/publishing/TikTokPostInfoFields';
import { useTikTokPostInfo } from '@/hooks/use-tiktok-post-info';
import { useCarouselExport } from '@/hooks/use-carousel-export';
import { useUploadFile } from '@/hooks/use-upload-file';
import { useRegisterUserMedia } from '@/hooks/use-register-user-media';
import { MY_SOCIAL_CONNECTIONS } from '@/queries/queries';
import {
  PUBLISH_CLIP_TO_FACEBOOK, PUBLISH_CLIP_TO_INSTAGRAM, PUBLISH_CLIP_TO_TIKTOK,
} from '@/queries/publishing';
import { CREATE_MY_SCHEDULED_PUBLISH } from '@/queries/scheduledPublishing';
import { datetimeLocalValueToIso, viewerTimeZone } from '@/lib/publishing/calendarMonth';
import { isScheduledAtTooSoon } from '@/lib/publishing/isScheduledAtTooSoon';
import { minScheduleDatetimeLocal } from '@/lib/publishing/minScheduleDatetimeLocal';
import {
  isDirectPostConnection,
  isFacebookPublishableConnection,
  isInstagramPublishableConnection,
} from '@/lib/publishing/resolvePublishEligibility';
import { extractPublishErrorCode, mapPublishError } from '@/lib/publishing/mapPublishError';
import type { PublishConnection } from '@/lib/publishing/types';
import { frameDataUrlToFile } from '@/lib/media/frameCapture';
import {
  buildCarouselPublishInput,
  type CarouselPublishPlatform,
} from '@/lib/quoteCarousel/buildCarouselPublishInput';
import type { QuoteSlideProps } from '@/lib/quoteCarousel/buildQuoteSlideProps';
import {
  INSTAGRAM_SAFE_FORMAT,
  isFormatAllowedForPlatform,
} from '@/lib/quoteCarousel/isFormatAllowedForPlatform';
import type { QuoteCarouselFormat } from '@/lib/quoteCarousel/types';

const PUBLISHABLE_PREDICATES: Record<CarouselPublishPlatform, (connection: PublishConnection) => boolean> = {
  instagram: isInstagramPublishableConnection,
  facebook: isFacebookPublishableConnection,
  tiktok: isDirectPostConnection,
};

/** Instagram and Facebook caption cap; TikTok photo posts allow a 4000-char description. */
const CAPTION_LIMITS: Record<CarouselPublishPlatform, number> = {
  instagram: 2200,
  facebook: 2200,
  tiktok: 4000,
};

type Stage = 'idle' | 'rendering' | 'uploading' | 'publishing';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: CarouselPublishPlatform;
  clipId: string;
  clipName: string;
  initialCaption: string;
  /** One entry per slide, already built without the watermark (publishing is PRO). */
  slideProps: QuoteSlideProps[];
  /** Current carousel format; Instagram rejects 9:16 images. */
  format: QuoteCarouselFormat;
  /** Switches the carousel format (the one-click "use 4:5" fix). */
  onFormatChange: (format: QuoteCarouselFormat) => void;
  /** Flushes the pending carousel draft before rendering. */
  onBeforePublish?: () => void;
}

function dataUrlToJpegFile(dataUrl: string, index: number): File {
  const raw = frameDataUrlToFile(dataUrl, index);
  return new File([raw], `carousel-slide-${index + 1}.jpg`, { type: raw.type || 'image/jpeg' });
}

/**
 * Publishes the quote carousel as an image post: Instagram or Facebook Page
 * (now or later) or TikTok (now, Direct Post only — requires `video.publish`). On submit it
 * flushes the draft, renders every slide as JPEG, uploads each one, registers
 * it in My Media, then calls the existing publish mutation with `imageUrls`.
 * Uploaded URLs are reused on a retry while the slides are unchanged.
 */
export const PublishCarouselDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  platform,
  clipId,
  clipName,
  initialCaption,
  slideProps,
  format,
  onFormatChange,
  onBeforePublish,
}) => {
  const { t } = useTranslation();
  const captionLimit = CAPTION_LIMITS[platform];
  const [connectionId, setConnectionId] = useState('');
  const [caption, setCaption] = useState('');
  const [when, setWhen] = useState<PublishWhen>('now');
  const [datetimeLocal, setDatetimeLocal] = useState('');
  const [whenError, setWhenError] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('idle');
  const [uploaded, setUploaded] = useState(0);
  const uploadedRef = useRef<{ key: string; urls: string[] } | null>(null);
  const mountedRef = useRef(true);
  // Seed the caption once per open; later prop changes must not clobber edits.
  const initialCaptionRef = useRef(initialCaption);
  initialCaptionRef.current = initialCaption;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const { data: connData } = useQuery(MY_SOCIAL_CONNECTIONS, { skip: !open });
  const [publishInstagram] = useMutation(PUBLISH_CLIP_TO_INSTAGRAM);
  const [publishFacebook] = useMutation(PUBLISH_CLIP_TO_FACEBOOK);
  const [publishTikTok] = useMutation(PUBLISH_CLIP_TO_TIKTOK);
  const [createScheduled] = useMutation(CREATE_MY_SCHEDULED_PUBLISH);
  const { progress, renderAll, cancel } = useCarouselExport();
  const { getUploadUrl } = useUploadFile();
  const registerUserMedia = useRegisterUserMedia();

  const connections: PublishConnection[] = useMemo(
    () => (connData?.mySocialConnections || [])
      .filter((c: PublishConnection) => c.platform === platform),
    [connData, platform],
  );
  const publishable = useMemo(
    () => connections.filter(PUBLISHABLE_PREDICATES[platform]),
    [connections, platform],
  );
  const selected = connectionId || publishable[0]?.id || '';
  const selectedConnection = publishable.find((connection) => connection.id === selected);
  const postInfo = useTikTokPostInfo({ open: open && platform === 'tiktok', socialConnectionId: selected });
  const { reset: resetPostInfo } = postInfo;

  useEffect(() => {
    if (!open) return;
    setConnectionId('');
    setCaption(initialCaptionRef.current.slice(0, captionLimit));
    setWhen('now');
    setDatetimeLocal('');
    setWhenError(null);
    setErrorKey(null);
    setStage('idle');
    resetPostInfo();
  }, [open, platform, clipId, captionLimit, resetPostInfo]);

  const timeZone = viewerTimeZone();
  const minDatetimeLocal = minScheduleDatetimeLocal(new Date(), timeZone);
  const busy = stage !== 'idle';

  const close = () => {
    if (busy) return;
    cancel();
    onOpenChange(false);
  };

  const goToSettings = () => {
    onOpenChange(false);
    window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', {
      detail: { section: 'social', platform },
    }));
  };

  const formatAllowed = isFormatAllowedForPlatform(format, platform);

  const supportsLater = platform !== 'tiktok';

  const canSubmit = !busy
    && formatAllowed
    && !!selected
    && slideProps.length > 0
    && caption.length <= captionLimit
    && (supportsLater
      ? (when === 'now' || !!datetimeLocal)
      : postInfo.isComplete);

  /** Renders + uploads every slide, reusing the last upload when unchanged. */
  const prepareImageUrls = async (): Promise<string[] | null> => {
    const key = JSON.stringify(slideProps);
    if (uploadedRef.current?.key === key) return uploadedRef.current.urls;

    setStage('rendering');
    const rendered = await renderAll(slideProps, 'jpeg');
    if (!mountedRef.current) return null;
    if (rendered.some((url) => url === null)) {
      setErrorKey('quoteCarousel.publish.errors.renderFailed');
      return null;
    }

    setStage('uploading');
    setUploaded(0);
    const urls: string[] = [];
    for (let index = 0; index < rendered.length; index += 1) {
      let url: string | null = null;
      try {
        url = await getUploadUrl(dataUrlToJpegFile(rendered[index] as string, index));
      } catch {
        url = null;
      }
      if (!mountedRef.current) return null;
      if (!url || !url.startsWith('https://')) {
        setErrorKey('quoteCarousel.publish.errors.uploadFailed');
        return null;
      }
      const { width, height } = slideProps[index];
      registerUserMedia({
        url,
        kind: 'image',
        name: `Carrossel – ${clipName} – ${index + 1}`,
        width,
        height,
      });
      urls.push(url);
      setUploaded(index + 1);
    }
    uploadedRef.current = { key, urls };
    return urls;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setErrorKey(null);

    let scheduledAt: string | null = null;
    if (supportsLater && when === 'later') {
      scheduledAt = datetimeLocalValueToIso(datetimeLocal, timeZone);
      if (!scheduledAt || isScheduledAtTooSoon(scheduledAt, new Date())) {
        setWhenError(t('publishing.schedule.tooSoon'));
        return;
      }
    }

    onBeforePublish?.();
    try {
      const imageUrls = await prepareImageUrls();
      if (!imageUrls || !mountedRef.current) return;

      const request = buildCarouselPublishInput({
        platform,
        clipId,
        socialConnectionId: selected,
        imageUrls,
        caption: caption.trim(),
        when: scheduledAt ? { mode: 'later', scheduledAt } : { mode: 'now' },
        tiktok: platform === 'tiktok'
          ? {
            privacyLevel: postInfo.variables.privacyLevel,
            disableComment: postInfo.variables.disableComment,
            brandContentToggle: postInfo.variables.brandContentToggle,
            brandOrganicToggle: postInfo.variables.brandOrganicToggle,
          }
          : undefined,
      });

      setStage('publishing');
      let record: { status?: string | null; fail_reason?: string | null } | null | undefined;
      if (request.kind === 'schedule') {
        await createScheduled({ variables: request.variables });
      } else if (request.kind === 'facebook_now') {
        const result = await publishFacebook({ variables: request.variables });
        record = result.data?.publishClipToFacebook;
      } else if (request.kind === 'instagram_now') {
        const result = await publishInstagram({ variables: request.variables });
        record = result.data?.publishClipToInstagram;
      } else {
        const result = await publishTikTok({ variables: request.variables });
        record = result.data?.publishClipToTikTok;
      }
      if (!mountedRef.current) return;

      if (record?.status === 'failed') {
        setErrorKey(mapPublishError(record.fail_reason));
        return;
      }
      toast.success(request.kind === 'schedule'
        ? t('publishing.schedule.saved')
        : t('quoteCarousel.publish.started', { platform: t(`publishing.schedule.platforms.${platform}`) }));
      setStage('idle');
      onOpenChange(false);
    } catch (err) {
      if (!mountedRef.current) return;
      setErrorKey(mapPublishError(extractPublishErrorCode(err)));
    } finally {
      if (mountedRef.current) setStage('idle');
    }
  };

  const total = slideProps.length;
  const rendered = progress.filter((entry) => entry.status === 'done').length;
  const stageText = stage === 'rendering'
    ? t('quoteCarousel.publish.stage.rendering', { done: rendered, total })
    : stage === 'uploading'
      ? t('quoteCarousel.publish.stage.uploading', { done: uploaded, total })
      : stage === 'publishing'
        ? t('quoteCarousel.publish.stage.publishing')
        : null;

  const blockDismiss = (event: Event) => {
    if (busy) event.preventDefault();
  };

  const platformLabel = t(`publishing.schedule.platforms.${platform}`);

  const body = () => {
    if (connections.length === 0) {
      return (
        <div className="space-y-3" data-testid="carousel-publish-no-connection">
          <p className="text-sm text-muted-foreground">
            {platform === 'facebook'
              ? t('quoteCarousel.publish.noConnectionFacebook')
              : t('quoteCarousel.publish.noConnection', { platform: platformLabel })}
          </p>
          <Button type="button" onClick={goToSettings}>{t('publishing.reconnectAction')}</Button>
        </div>
      );
    }
    if (publishable.length === 0) {
      return (
        <div className="space-y-3" data-testid="carousel-publish-reconnect">
          <p className="text-sm text-muted-foreground">
            {platform === 'tiktok' && t('quoteCarousel.publish.tiktokReconnect')}
            {platform === 'instagram' && t('publishing.instagram.reconnectBody')}
            {platform === 'facebook' && t('publishing.facebook.reconnectBody')}
          </p>
          <Button type="button" onClick={goToSettings}>{t('publishing.reconnectAction')}</Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {!formatAllowed && (
          <div
            className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs"
            data-testid="carousel-publish-format-blocked"
          >
            <p>{t('quoteCarousel.publish.instagramFormatBlocked')}</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="carousel-publish-switch-format"
              disabled={busy}
              onClick={() => onFormatChange(INSTAGRAM_SAFE_FORMAT)}
            >
              {t('quoteCarousel.publish.switchTo4x5')}
            </Button>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="carousel-publish-account">{t('publishing.accountLabel')}</Label>
          <Select value={selected} onValueChange={setConnectionId} disabled={busy}>
            <SelectTrigger id="carousel-publish-account" data-testid="carousel-publish-account">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {publishable.map((connection) => (
                <SelectItem key={connection.id} value={connection.id}>
                  {connection.display_name || connection.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {platform === 'tiktok' && (
          <TikTokCreatorStatus
            postInfo={postInfo}
            fallbackName={selectedConnection?.display_name || selectedConnection?.username}
            onReconnect={goToSettings}
          />
        )}

        <div className="space-y-1.5">
          <Label htmlFor="carousel-publish-caption">{t('quoteCarousel.publish.captionLabel')}</Label>
          <Textarea
            id="carousel-publish-caption"
            data-testid="carousel-publish-caption"
            value={caption}
            maxLength={captionLimit}
            rows={4}
            disabled={busy}
            onChange={(event) => setCaption(event.target.value.slice(0, captionLimit))}
          />
          <p className="text-xs text-muted-foreground">
            {t('publishing.youtube.characterCount', { current: caption.length, max: captionLimit })}
          </p>
        </div>

        {supportsLater ? (
          <PublishWhenControl
            idPrefix="carousel"
            when={when}
            onWhenChange={(next) => {
              setWhen(next);
              setWhenError(null);
              if (next === 'later' && !datetimeLocal) setDatetimeLocal(minDatetimeLocal);
            }}
            datetimeLocal={datetimeLocal}
            onDatetimeLocalChange={(value) => {
              setDatetimeLocal(value);
              setWhenError(null);
            }}
            minDatetimeLocal={minDatetimeLocal}
            error={whenError}
          />
        ) : (
          <TikTokPostInfoFields postInfo={postInfo} photoPost>
            <p className="text-xs text-muted-foreground">{t('quoteCarousel.publish.tiktokNowOnly')}</p>
          </TikTokPostInfoFields>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        hideCloseButton={busy}
        onEscapeKeyDown={blockDismiss}
        onPointerDownOutside={blockDismiss}
        onInteractOutside={blockDismiss}
        data-testid="carousel-publish-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('quoteCarousel.publish.title', { platform: platformLabel })}</DialogTitle>
          <DialogDescription>
            {t('quoteCarousel.publish.description', { count: total })}
          </DialogDescription>
        </DialogHeader>
        {body()}
        {stageText && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="carousel-publish-stage">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            {stageText}
          </p>
        )}
        {errorKey && (
          <p
            className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
            data-testid="carousel-publish-error"
          >
            {t(errorKey)}
          </p>
        )}
        {publishable.length > 0 && (
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close} disabled={busy}>
              {t('publishing.instagram.cancel')}
            </Button>
            <Button
              type="button"
              data-testid="carousel-publish-submit"
              disabled={!canSubmit}
              onClick={() => { void handleSubmit(); }}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {supportsLater && when === 'later'
                ? t('quoteCarousel.publish.schedule')
                : t('quoteCarousel.publish.submit')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PublishCarouselDialog;
